import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthState } from '@meddev/fe-shared';
import { Store } from '@ngxs/store';
import { BookingModel, BookingState } from '@shared/booking-feature';
import { APP_CONSTANTS, APP_ROUTE_CONSTANTS, AppRoutingManagerService } from '@shared/core';
import { combineLatest, filter, first, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { CallStatusType } from '../enum/call-status-type.enum';
import { CallData, DeviceInfo } from '../interface';
import { CallActions, CallState, MediaActions, MediaState } from '../store';
import { MEDIA_ADAPTER } from '../token/media.adapter.token';

@Injectable({ providedIn: 'root' })
export class MediaManagerService {
    private readonly store = inject(Store);
    private readonly mediaAdapter = inject(MEDIA_ADAPTER);
    private readonly router = inject(Router);
    private readonly appRoutingManager = inject(AppRoutingManagerService);
    private readonly appRouteConstants = inject(APP_ROUTE_CONSTANTS);
    private readonly appConstants = inject(APP_CONSTANTS);

    callStatus$ = this.store.select(CallState.callStatus);
    callActive$ = this.store.select(CallState.callActive);
    chatOpen$ = this.store.select(CallState.chatOpen);
    settingsOpen$ = this.store.select(CallState.settingsOpen);

    isMicOn$ = this.store.select(MediaState.isMicOn);
    isCameraOn$ = this.store.select(MediaState.isCameraOn);
    isAudioOn$ = this.store.select(MediaState.isAudioOn);
    audioVolume$ = this.store.select(MediaState.audioVolume);
    selectedMicDevice$ = this.store.select(MediaState.selectedMicDevice);
    selectedCameraDevice$ = this.store.select(MediaState.selectedCameraDevice);
    selectedAudioDevice$ = this.store.select(MediaState.selectedAudioDevice);

    micDevices$ = this.store.select(MediaState.micDevices);
    audioDevices$ = this.store.select(MediaState.audioDevices);
    cameraDevices$ = this.store.select(MediaState.cameraDevices);

    mediaInitialized$ = this.store.select(MediaState.mediaInitialized);

    booking$ = this.store.select(BookingState.booking);

    setGuid() {
        this.mediaAdapter.setGuid();
    }

    initLocalMedia() {
        this.store.dispatch(new MediaActions.InitLocalMedia());
        return this.mediaAdapter.initLocalMedia().pipe(
            switchMap(() => combineLatest([this.isMicOn$, this.isCameraOn$, this.audioVolume$])),
            switchMap(([isMicOn, isCameraOn, audioVolume]) => {
                return forkJoin([
                    this.mediaAdapter.setMic(isMicOn),
                    this.mediaAdapter.setCamera(isCameraOn),
                    this.mediaAdapter.setAudio(audioVolume),
                ]);
            }),
        );
    }

    destroyLocalMedia() {
        this.mediaAdapter.destroyLocalMedia();
        return this.store.dispatch([new CallActions.SetCallStatus(null), new MediaActions.DestroyLocalMedia()]);
    }

    loadDevices() {
        combineLatest([this.mediaAdapter.getMicDevices(), this.mediaAdapter.getCameraDevices(), this.mediaAdapter.getAudioDevices()])
            .pipe(
                tap(([micDevices, cameraDevices, audioDevices]) => {
                    this.store.dispatch([
                        new MediaActions.SetMicDevices(micDevices),
                        new MediaActions.SetCameraDevices(cameraDevices),
                        new MediaActions.SetAudioDevices(audioDevices),
                    ]);
                }),
            )
            .subscribe();
    }

    toggleMic() {
        this.store
            .selectOnce(MediaState.isMicOn)
            .pipe(
                map(isOn => !isOn),
                switchMap(isOn => this.mediaAdapter.setMic(isOn)),
                switchMap(() => this.store.dispatch(new MediaActions.ToggleMic())),
            )
            .subscribe();
    }

    toggleCamera() {
        this.store
            .selectOnce(MediaState.isCameraOn)
            .pipe(
                map(isOn => !isOn),
                switchMap(isOn => this.mediaAdapter.setCamera(isOn)),
                switchMap(() => this.store.dispatch(new MediaActions.ToggleCamera())),
            )
            .subscribe();
    }

    toggleAudio() {
        this.store
            .selectOnce(MediaState.audioVolume)
            .pipe(
                map(volume => (volume === 0 ? 100 : 0)),
                switchMap(volume => this.mediaAdapter.setAudio(volume)),
                switchMap(() => this.store.dispatch(new MediaActions.ToggleAudio())),
            )
            .subscribe();
    }

    selectMicDevice(device: DeviceInfo) {
        this.mediaAdapter
            .setMicDevice(device)
            .pipe(switchMap(() => this.store.dispatch(new MediaActions.SetSelectedMicDevice(device))))
            .subscribe();
    }

    selectCameraDevice(device: DeviceInfo) {
        this.mediaAdapter
            .setCameraDevice(device)
            .pipe(switchMap(() => this.store.dispatch(new MediaActions.SetSelectedCameraDevice(device))))
            .subscribe();
    }

    selectAudioDevice(device: DeviceInfo) {
        this.mediaAdapter
            .setAudioDevice(device)
            .pipe(switchMap(() => this.store.dispatch(new MediaActions.SetSelectedAudioDevice(device))))
            .subscribe();
    }

    toggleChat() {
        this.store.dispatch(new CallActions.ToggleChat());
    }

    toggleSettings() {
        this.store.dispatch(new CallActions.ToggleSettings());
    }

    startCall() {
        const appId = this.appConstants.environment?.agora?.appId;

        if (!appId) {
            console.error('Agora appId is not defined in environment');
            return;
        }

        this.booking$
            .pipe(
                first(),
                filter((booking): booking is BookingModel => !!booking?.prereservationId),
                switchMap(booking => {
                    const channel = booking.prereservationId ?? '';

                    return this.store.selectOnce(MediaState.mediaInitialized).pipe(
                        switchMap(mediaInitialized => {
                            if (!mediaInitialized) {
                                return this.initLocalMedia();
                            }
                            return of(true);
                        }),
                        switchMap(() => {
                            return this.store.select(AuthState.tokenData$);
                        }),
                        filter(tokenData => !!tokenData?.value?.accessTokenV2),
                        map(tokenData => tokenData?.value?.accessTokenV2),
                        switchMap(token => {
                            if (token) {
                                const data: CallData = { appId, channel, contractorId: '0', token };
                                this.store.dispatch(
                                    new CallActions.SetCallStatus({
                                        status: CallStatusType.CALL_STARTING,
                                        color: 'warning',
                                    }),
                                );
                                return this.mediaAdapter.startCall(data);
                            }
                            return of(CallStatusType.CALL_ERROR);
                        }),
                        tap(callStatus => {
                            switch (callStatus) {
                                case CallStatusType.CALL_STARTED:
                                    this.store.dispatch(new CallActions.StartCall());
                                    this.store.dispatch(
                                        new CallActions.SetCallStatus({
                                            status: CallStatusType.CALL_STARTED,
                                            color: 'success',
                                        }),
                                    );
                                    this.router.navigate([this.appRouteConstants['telemedicineCall']]);
                                    break;

                                case CallStatusType.CALL_ERROR:
                                    this.store.dispatch(
                                        new CallActions.SetCallStatus({
                                            status: CallStatusType.CALL_ERROR,
                                            color: 'danger',
                                        }),
                                    );
                                    break;

                                case CallStatusType.REMOTE_PREPARING:
                                    this.store.dispatch(
                                        new CallActions.SetCallStatus({
                                            status: CallStatusType.REMOTE_PREPARING,
                                            color: 'warning',
                                        }),
                                    );
                                    break;

                                case CallStatusType.REMOTE_LEFT:
                                    this.store.dispatch(
                                        new CallActions.SetCallStatus({
                                            status: CallStatusType.REMOTE_LEFT,
                                            color: 'danger',
                                        }),
                                    );
                                    break;

                                case CallStatusType.REMOTE_DEVICE_ISSUES:
                                    this.store.dispatch(
                                        new CallActions.SetCallStatus({
                                            status: CallStatusType.REMOTE_DEVICE_ISSUES,
                                            color: 'warning',
                                        }),
                                    );
                                    break;

                                case CallStatusType.REMOTE_MUTED_DEVICES:
                                    this.store.dispatch(
                                        new CallActions.SetCallStatus({
                                            status: CallStatusType.REMOTE_MUTED_DEVICES,
                                            color: 'warning',
                                        }),
                                    );
                                    break;

                                case CallStatusType.REMOTE_CONNECTING:
                                    this.store.dispatch(
                                        new CallActions.SetCallStatus({
                                            status: CallStatusType.REMOTE_CONNECTING,
                                            color: 'warning',
                                        }),
                                    );
                                    break;

                                case CallStatusType.CALL_STARTING:
                                    this.store.dispatch(
                                        new CallActions.SetCallStatus({
                                            status: CallStatusType.CALL_STARTING,
                                            color: 'warning',
                                        }),
                                    );
                                    break;

                                case CallStatusType.WAITING_FOR_REMOTE:
                                    this.store.dispatch(
                                        new CallActions.SetCallStatus({
                                            status: CallStatusType.WAITING_FOR_REMOTE,
                                            color: 'warning',
                                        }),
                                    );
                                    break;
                            }
                        }),
                    );
                }),
            )
            .subscribe();
    }

    endCall() {
        this.mediaAdapter
            .endCall()
            .pipe(
                first(),
                tap(() => {
                    this.store.dispatch([new CallActions.EndCall(), new CallActions.SetCallStatus(null)]);
                }),
                switchMap(() =>
                    this.booking$.pipe(
                        first(),
                        tap(booking => {
                            if (booking?.id) {
                                this.appRoutingManager.toBookingDetails(booking.id);
                            } else {
                                this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                                    this.router.navigate([this.appRouteConstants['telemedicineLobby']]);
                                });
                            }
                        }),
                    ),
                ),
            )
            .subscribe();
    }
}
